import React from "react";
import { ArrowUp, Linkedin, Facebook, Instagram, MessageCircle, Phone, Mail, Globe, MapPin } from "lucide-react";
import { MagneticButton } from "../helpers";

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

const QUICK = [
  { label: "Home", id: "home" },
  { label: "Services", id: "services" },
  { label: "Why Us", id: "why" },
  { label: "About", id: "about" },
  { label: "Contact", id: "contact" },
];

const SOCIALS = [
  { Icon: Instagram, href: "https://www.instagram.com/paramarsh_tech", label: "Instagram" },
  { Icon: Facebook, href: "https://www.facebook.com/share/1H5ndnhbgU/", label: "Facebook" },
  { Icon: Linkedin, href: "https://www.linkedin.com/company/paramarshtech/", label: "LinkedIn" },
  { Icon: MessageCircle, href: "https://wa.me/+917863819886", label: "WhatsApp" },
];

const SERVICES = ["Web Development", "App Development", "AI Automation", "Digital Consultation"];

export default function Footer() {
  return (
    <footer className="relative bg-[#050505] pt-24 pb-12">
      <div className="absolute top-0 left-0 right-0 h-px" style={{ background: "linear-gradient(to right,transparent,#FF8A00,transparent)" }} />
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-8">
          <div className="md:col-span-2 lg:col-span-4 lg:pr-8">
            <div className="flex items-center gap-3">
              <img src="/paramarsh-mark.webp" alt="Paramarsh Tech" className="h-12 w-12 object-cover rounded-xl border border-brand-orange/40 shadow-[0_0_18px_rgba(255,138,0,0.3)]" />
              <img src="/paramarsh-wordmark.png" alt="Paramarsh Tech" className="h-11 w-auto object-contain" />
            </div>
            <p className="mt-5 font-body text-sm text-brand-muted leading-relaxed max-w-xs">
              A creative digital & AI agency building modern, scalable and intelligent solutions
              for a better tomorrow.
            </p>
            <div className="mt-6 flex gap-3">
              {SOCIALS.map(({ Icon, href, label }, i) => (
                <a
                  key={i}
                  data-testid={`footer-social-${i}`}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={label}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full glass border border-white/10 hover:border-brand-orange/40 hover:text-brand-orange text-brand-sec transition-colors"
                >
                  <Icon className="h-4 w-4" strokeWidth={1.5} />
                </a>
              ))}
            </div>
          </div>

          <div className="lg:col-span-2 lg:pl-10">
            <h4 className="font-heading font-medium text-sm uppercase tracking-widest text-white mb-5">Quick Links</h4>
            <ul className="space-y-3">
              {QUICK.map((q) => (
                <li key={q.id}>
                  <button data-testid={`footer-link-${q.id}`} onClick={() => scrollTo(q.id)} className="font-body text-sm text-brand-muted hover:text-brand-orange transition-colors">
                    {q.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3 lg:pl-6">
            <h4 className="font-heading font-medium text-sm uppercase tracking-widest text-white mb-5">Services</h4>
            <ul className="space-y-3">
              {SERVICES.map((s) => (
                <li key={s} className="font-body text-sm text-brand-muted">{s}</li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <h4 className="font-heading font-medium text-sm uppercase tracking-widest text-white mb-5">Get in touch</h4>
            <ul className="space-y-3.5 font-mono text-sm text-brand-muted">
              <li>
                <a href="tel:+917863819886" className="flex items-center gap-3 hover:text-brand-orange transition-colors">
                  <Phone className="h-4 w-4 text-brand-orange shrink-0" strokeWidth={1.5} /> +91 78638 19886
                </a>
              </li>
              <li>
                <a href="mailto:paramarshtech0@gmail.com" className="flex items-center gap-3 hover:text-brand-orange transition-colors">
                  <Mail className="h-4 w-4 text-brand-orange shrink-0" strokeWidth={1.5} /> <span className="break-all">paramarshtech0@gmail.com</span>
                </a>
              </li>
              <li>
                <a href="https://paramarshtech.co.in" target="_blank" rel="noreferrer" className="flex items-center gap-3 hover:text-brand-orange transition-colors">
                  <Globe className="h-4 w-4 text-brand-orange shrink-0" strokeWidth={1.5} /> paramarshtech.co.in
                </a>
              </li>
              <li className="flex items-center gap-3">
                <MapPin className="h-4 w-4 text-brand-orange shrink-0" strokeWidth={1.5} /> Surat, Gujarat
              </li>
              <li className="flex items-center gap-3">
                <MapPin className="h-4 w-4 text-brand-orange shrink-0" strokeWidth={1.5} /> Valsad, Gujarat
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="font-body text-xs text-brand-muted">© {new Date().getFullYear()} Paramarsh Tech. All rights reserved.</p>
          <MagneticButton
            data-testid="back-to-top"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="inline-flex items-center gap-2 rounded-full glass border border-white/10 px-5 py-2.5 text-xs font-body text-brand-sec hover:text-brand-orange hover:border-brand-orange/40 transition-colors"
          >
            Back to Top
            <ArrowUp className="h-3.5 w-3.5" strokeWidth={2} />
          </MagneticButton>
        </div>
      </div>
    </footer>
  );
}
